/**
 * Color utilities — deterministic color generation from strings
 * and contrast-aware text color selection.
 *
 * Used by avatar system to generate consistent colors from emails.
 * Ported from v1's `color-handling.ts`.
 */

/**
 * Generate a deterministic hex color from any string (typically an email).
 * Same input always produces the same color.
 */
export function stringToColor(text: string): string {
  if (text == null) return "#000000"
  let hash = 0
  for (let i = 0; i < text.length; i++) {
    hash = text.charCodeAt(i) + ((hash << 5) - hash)
  }
  let color = "#"
  for (let i = 0; i < 3; i++) {
    const value = (hash >> (i * 8)) & 0xff
    color += ("00" + value.toString(16)).slice(-2)
  }
  return color
}

/**
 * Returns black or white depending on the luminance of the input hex color.
 * Used to ensure text on a colored avatar background is always readable.
 */
export function invertColor(hex: string): string {
  let h = hex
  if (h.indexOf("#") === 0) h = h.slice(1)
  if (h.length === 3) {
    h =
      (h[0] ?? "0") +
      (h[0] ?? "0") +
      (h[1] ?? "0") +
      (h[1] ?? "0") +
      (h[2] ?? "0") +
      (h[2] ?? "0")
  }
  if (h.length !== 6) return "#000000"
  const r = parseInt(h.slice(0, 2), 16)
  const g = parseInt(h.slice(2, 4), 16)
  const b = parseInt(h.slice(4, 6), 16)
  // W3C perceived luminance formula
  return r * 0.299 + g * 0.587 + b * 0.114 > 186 ? "#000000" : "#FFFFFF"
}

/**
 * Blend an RGBA foreground color onto an opaque background.
 * Both colors must be in CSS `rgba(r, g, b, a)` or `rgb(r, g, b)` format.
 * Returns a hex string.
 */
export function hexify(color: string, background: string): string {
  const bgParts = background
    .replace("rgba(", "")
    .replace("rgb(", "")
    .replace(")", "")
    .split(",")
    .map((x) => parseInt(x))
  const bg_r = bgParts[0] ?? 255
  const bg_g = bgParts[1] ?? 255
  const bg_b = bgParts[2] ?? 255

  const values = color
    .replace(/rgba?\(/, "")
    .replace(/\)/, "")
    .replace(/[\s+]/g, "")
    .split(",")
  const a = parseFloat(values[3] || "1")
  const r = Math.floor(a * parseInt(values[0] ?? "0") + (1 - a) * bg_r)
  const g = Math.floor(a * parseInt(values[1] ?? "0") + (1 - a) * bg_g)
  const b = Math.floor(a * parseInt(values[2] ?? "0") + (1 - a) * bg_b)

  return (
    "#" +
    ("0" + r.toString(16)).slice(-2) +
    ("0" + g.toString(16)).slice(-2) +
    ("0" + b.toString(16)).slice(-2)
  )
}

/**
 * Mix a hex color with a given alpha on a white background.
 * Returns a hex string. Simpler than hexify for common sidebar use.
 */
export function alphaBlend(hex: string, alpha: number): string {
  let h = hex
  if (h.indexOf("#") === 0) h = h.slice(1)
  if (h.length === 3) {
    h =
      (h[0] ?? "0") +
      (h[0] ?? "0") +
      (h[1] ?? "0") +
      (h[1] ?? "0") +
      (h[2] ?? "0") +
      (h[2] ?? "0")
  }
  const r = Math.round(parseInt(h.slice(0, 2), 16) * alpha + 255 * (1 - alpha))
  const g = Math.round(parseInt(h.slice(2, 4), 16) * alpha + 255 * (1 - alpha))
  const b = Math.round(parseInt(h.slice(4, 6), 16) * alpha + 255 * (1 - alpha))
  return (
    "#" +
    ("0" + r.toString(16)).slice(-2) +
    ("0" + g.toString(16)).slice(-2) +
    ("0" + b.toString(16)).slice(-2)
  )
}
